import { findBand, BAND_COLORS } from "../bands";

// SLEDAI (SELENA modification) — SLE Disease Activity Index.
// 24 weighted descriptors (weights 8 / 4 / 2 / 1); score only items present in
// the last 10 days and attributable to SLE. Total 0–105.
// ⚠️ Verify descriptor definitions/weights clinically before use.
// ⚠️ This order is a persistence contract: field keys are positional (`i0`…`i23`)
// and saved scores store them, so reordering or removing an item silently
// rehydrates old visits onto the wrong descriptors. Only append.
const ITEMS = [
    { w: 8, label: "Seizure", hint: "Recent onset. Exclude metabolic, infectious or drug cause." },
    { w: 8, label: "Psychosis", hint: "Altered ability to function in normal activity due to severe disturbance in the perception of reality." },
    { w: 8, label: "Organic brain syndrome", hint: "Altered mental function with impaired orientation, memory or other intellectual function, with rapid onset and fluctuating features." },
    { w: 8, label: "Visual disturbance", hint: "Retinal changes of SLE: cytoid bodies, retinal haemorrhages, serous exudate/haemorrhage in the choroid, or optic neuritis." },
    { w: 8, label: "Cranial nerve disorder", hint: "New onset of sensory or motor neuropathy involving cranial nerves." },
    { w: 8, label: "Lupus headache", hint: "Severe persistent headache: may be migrainous, but must be nonresponsive to narcotic analgesia." },
    { w: 8, label: "CVA", hint: "New onset of cerebrovascular accident(s). Exclude arteriosclerosis." },
    { w: 8, label: "Vasculitis", hint: "Ulceration, gangrene, tender finger nodules, periungual infarction, splinter haemorrhages, or biopsy/angiogram proof." },
    { w: 4, label: "Arthritis", hint: "More than 2 joints with pain and signs of inflammation (tenderness, swelling or effusion)." },
    { w: 4, label: "Myositis", hint: "Proximal muscle aching/weakness with elevated CPK/aldolase, EMG changes, or biopsy showing myositis." },
    { w: 4, label: "Urinary casts", hint: "Heme-granular or red blood cell casts." },
    { w: 4, label: "Hematuria", hint: ">5 red blood cells/high power field. Exclude stone, infection or other cause." },
    { w: 4, label: "Proteinuria", hint: ">0.5 gm/24 hours. New onset or recent increase of more than 0.5 gm/24 hours." },
    { w: 4, label: "Pyuria", hint: ">5 white blood cells/high power field. Exclude infection." },
    { w: 2, label: "New rash", hint: "New onset or recurrence of inflammatory type rash." },
    { w: 2, label: "Alopecia", hint: "New onset or recurrence of abnormal, patchy or diffuse loss of hair." },
    { w: 2, label: "Mucosal ulcers", hint: "New onset or recurrence of oral or nasal ulcerations." },
    { w: 2, label: "Pleurisy", hint: "Pleuritic chest pain with pleural rub or effusion, or pleural thickening." },
    { w: 2, label: "Pericarditis", hint: "Pericardial pain with at least 1 of: rub, effusion, or EKG confirmation." },
    { w: 2, label: "Low complement", hint: "Decrease in CH50, C3, or C4 below the lower limit of normal for the testing laboratory." },
    { w: 2, label: "Increased DNA binding", hint: ">25% binding by Farr assay or above normal range for the testing laboratory." },
    { w: 1, label: "Fever", hint: ">38°C. Exclude infectious cause." },
    { w: 1, label: "Thrombocytopenia", hint: "<100,000 platelets/mm³." },
    { w: 1, label: "Leukopenia", hint: "<3,000 white blood cells/mm³. Exclude drug causes." },
];

const sledaiBands = [
    { label: "No flare present", color: BAND_COLORS.remission, lt: 1 },
    { label: "Mild activity", color: BAND_COLORS.low, lt: 6 },
    { label: "Moderate activity", color: BAND_COLORS.moderate, lt: 11 },
    { label: "High activity", color: BAND_COLORS.high, lt: 20 },
    { label: "Very high activity", color: BAND_COLORS.veryHigh },
];

const FIELDS = ITEMS.map((it, i) => ({
    key: `i${i}`,
    type: "toggle",
    label: it.label,
    hint: it.hint,
    default: 0,
}));

export const sledai = {
    id: "sledai",
    name: "SLEDAI (SELENA modification)",
    subtitle: "SLE disease activity index",
    group: "Systemic lupus erythematosus",
    status: "ready",
    layout: "compact",
    options: [],
    fields: FIELDS,
    compute: (v) => ITEMS.reduce((sum, it, i) => sum + (v[`i${i}`] ? it.w : 0), 0),
    interpret: (score) => findBand(score, sledaiBands),
    bands: () => sledaiBands,
    about:
        "The SELENA-SLEDAI disease activity index was developed for the SELENA trial and has since been validated many times. It is recommended by current SLE management recommendations as one of the options to follow disease activity of SLE patients in daily clinical practice. Each of 24 descriptors present in the last 10 days scores its weight (8, 4, 2 or 1); the total ranges from 0 to 105.",
    references: [
        "Petri M, et al. Combined oral contraceptives in women with systemic lupus erythematosus (SELENA). N Engl J Med. 2005.",
        "Bombardier C, et al. Derivation of the SLEDAI. Arthritis Rheum. 1992.",
    ],
};
